import React, { ButtonHTMLAttributes } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'outline-gray' | 'danger' | 'ghost' | 'dark' | 'gradient';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Button = ({
  children,
  variant = 'primary',
  size = 'lg',
  isLoading = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  className = '',
  disabled,
  ...props
}: ButtonProps) => {
  
  const baseClasses = 
    'inline-flex items-center justify-center font-semibold rounded-full transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed';

  const variantClasses = {
    primary: 'bg-brand text-white hover:bg-ultramarine-80 focus:ring-brand',
    secondary: 'bg-gray-10 text-gray-90 hover:bg-gray-20 focus:ring-gray-40',
    outline: 'border border-gray-40 bg-transparent text-gray-100 hover:bg-gray-10 focus:ring-gray-40',
    // New Variant: Gray Outline -> Black on Hover
    'outline-gray': 'border border-gray-50 bg-transparent text-gray-50 hover:border-gray-100 hover:text-gray-90 focus:ring-gray-30',
    danger: 'bg-error text-white hover:bg-red-70 focus:ring-error',
    ghost: 'bg-transparent text-gray-70 hover:text-gray-90 hover:bg-gray-10',
    dark: 'bg-gray-100 text-white hover:bg-black focus:ring-gray-80',
    gradient: 'bg-gradient-to-r from-[#12b9df] to-[#7a17f6] text-white shadow-xl hover:opacity-95 focus:ring-brand !rounded-xl',
  };

  const sizeClasses = {
    sm: 'h-9 px-3 py-2 text-sm',
    md: 'h-[42px] px-4 text-base',
    lg: 'h-[52px] px-8 text-base',
  };

  const widthClass = fullWidth ? 'w-full' : '';

  return (
    <button
      className={`
        ${baseClasses} 
        ${variantClasses[variant]} 
        ${sizeClasses[size]} 
        ${widthClass} 
        ${className}
      `}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <svg 
          className="animate-spin -ml-1 mr-2 h-5 w-5 text-current" 
          xmlns="http://www.w3.org/2000/svg" 
          fill="none" 
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      )}

      {!isLoading && leftIcon && <span className="mr-2.5">{leftIcon}</span>}
      {children}
      {!isLoading && rightIcon && <span className="ml-2.5">{rightIcon}</span>}
    </button>
  );
};
